import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { SEGMENTS, segmentLabel } from '../brand';
import { comparisonTableColumns } from '../keys';

/**
 * Block 02 — the two sides of the rule, side by side, with each one's share of the clicks that
 * could be classified at all.
 *
 * The two rows are two separate asks of Search Console, which is why they fail separately: one
 * side erroring leaves the other's figures on screen and withholds only the share, because a
 * share needs both halves of its denominator. The table never gains a total row — brand plus
 * non-brand is the known-query total, not the property's traffic, and a row called "Total" would
 * be read as the latter.
 *
 * The gap between those two totals is the queries Search Console withheld. It is named under the
 * table and never apportioned: a rule cannot classify a query it was never shown, so folding that
 * gap into either side would be inventing the answer the page exists to avoid inventing.
 */
function SegmentComparison({ t, scope, sides, rows, onExport, exportable }) {
    const columns = comparisonTableColumns(t, {
        hasPosition: reportsPosition(scope.searchType),
    });

    const loading = SEGMENTS.some((segment) => {
        const status = sides[segment]?.status;
        return status === 'idle' || status === 'loading';
    });
    const failed = SEGMENTS.filter((segment) => sides[segment]?.status === 'error');

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.brand_non_brand.comparison.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.brand_non_brand.comparison.description')} ·{' '}
                        {formatPeriod(scope.current)}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.brand_non_brand.coverage.export')}
                        aria-label={t('tools.brand_non_brand.coverage.export')}
                        onClick={onExport}
                        disabled={!exportable}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                {loading && <Loading padding={20} />}

                {/* One banner per failed side, naming it: "the request failed" is not an answer
                    when half the table is still showing numbers. */}
                {!loading &&
                    failed.map((segment) => (
                        <div key={segment} className="gsc-tool-banner error">
                            {t('tools.brand_non_brand.comparison.side_failed', {
                                segment: segmentLabel(t, segment),
                                reason: sides[segment]?.error ?? t('global.something_went_wrong'),
                            })}
                        </div>
                    ))}

                {!loading && failed.length < SEGMENTS.length && (
                    <>
                        <DataTable
                            nodes={rows}
                            COLUMNS={columns}
                            sortFns={getSortKeys(columns)}
                            additionalStyles={setAdditionalStyles(columns)}
                            onlyTable
                            customEmptyMessage={t('tools.brand_non_brand.comparison.empty')}
                            customContainerStyles="gsc-tool-key-table"
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.brand_non_brand.comparison.denominator_note')}
                        </p>
                        <p className="gsc-tool-table-note">
                            {t('tools.brand_non_brand.comparison.withheld_note')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(SegmentComparison);
